<template>
  <section id="intelligence" class="intelligence-section">
    <div class="intel-container">
      <div class="section-badge">
        <span class="ai-orb"></span>
        <span>{{ t.intelligence.badge }}</span>
      </div>

      <h2 class="section-title">{{ t.intelligence.title }}</h2>
      <span class="section-subtitle-gradient">{{ t.intelligence.tagline }}</span>

      <p class="section-subtitle">
        {{ t.intelligence.description }}
      </p>

      <!-- Interactive Siri Glow Simulator -->
      <div class="siri-glow-container" :class="{ 'siri-active': isSiriActive }">
        <div class="siri-phone-frame">
          <div class="phone-screen">
            <img
              :src="currentIntelImage"
              alt="Apple Intelligence in Action"
              class="screen-image"
            />

            <!-- Siri Glowing Edge Effect -->
            <div class="siri-edge-glow" v-if="isSiriActive"></div>

            <div class="dynamic-island">
              <span class="island-camera"></span>
            </div>
          </div>
        </div>

        <div class="siri-controller">
          <button @click="toggleSiri" class="siri-summon-btn" :class="{ active: isSiriActive }">
            <span class="siri-icon">✦</span>
            <span>{{ isSiriActive ? t.intelligence.siriActive : t.intelligence.siriInactive }}</span>
          </button>

          <div class="intel-tabs">
            <button
              v-for="(tab, index) in localizedIntelTabs"
              :key="index"
              @click="activeTabIndex = index"
              class="intel-tab-btn"
              :class="{ active: activeTabIndex === index }"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Bento Grid of Intelligence Features -->
      <div class="bento-grid">
        <div class="bento-card card-large">
          <div class="bento-text">
            <span class="card-eyebrow">{{ t.intelligence.writingEyebrow }}</span>
            <h3 class="card-title">{{ t.intelligence.writingTitle }}</h3>
            <p class="card-desc">
              {{ t.intelligence.writingDesc }}
            </p>
          </div>
          <div class="bento-media">
            <img
              src="https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-Apple-Intelligence-Writing-Tools-240610_inline.jpg.large.jpg"
              alt="Apple Writing Tools on iPhone"
              class="bento-img"
            />
          </div>
        </div>

        <div class="bento-card">
          <div class="bento-text">
            <span class="card-eyebrow">{{ t.intelligence.playgroundEyebrow }}</span>
            <h3 class="card-title">{{ t.intelligence.playgroundTitle }}</h3>
            <p class="card-desc">
              {{ t.intelligence.playgroundDesc }}
            </p>
          </div>
          <div class="bento-media">
            <img
              src="https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-Apple-Intelligence-Image-Playground-240610_inline.jpg.large.jpg"
              alt="Apple Image Playground"
              class="bento-img"
            />
          </div>
        </div>

        <div class="bento-card">
          <div class="bento-text">
            <span class="card-eyebrow">{{ t.intelligence.genmojiEyebrow }}</span>
            <h3 class="card-title">{{ t.intelligence.genmojiTitle }}</h3>
            <p class="card-desc">
              {{ t.intelligence.genmojiDesc }}
            </p>
          </div>
          <div class="bento-media">
            <img
              src="https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-Apple-Intelligence-Genmoji-240610_inline.jpg.large.jpg"
              alt="Apple Genmoji Creation"
              class="bento-img"
            />
          </div>
        </div>

        <div class="bento-card card-full">
          <div class="bento-text">
            <span class="card-eyebrow">{{ t.intelligence.siriEyebrow }}</span>
            <h3 class="card-title">{{ t.intelligence.siriTitle }}</h3>
            <p class="card-desc">
              {{ t.intelligence.siriDesc }}
            </p>
          </div>
          <div class="bento-media">
            <img
              src="https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-Apple-Intelligence-Siri-glow-240610_inline.jpg.large.jpg"
              alt="Siri Glowing Edge display on iPhone 16 Pro"
              class="bento-img"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useI18n } from '@/i18n';

export default {
  name: 'AppleIntelligenceSection',
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      isSiriActive: false,
      activeTabIndex: 0,
      intelImages: [
        'https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-Apple-Intelligence-Writing-Tools-240610_inline.jpg.large.jpg',
        'https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-Apple-Intelligence-Image-Playground-240610_inline.jpg.large.jpg',
        'https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-Apple-Intelligence-Genmoji-240610_inline.jpg.large.jpg',
        'https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-Apple-Intelligence-Siri-glow-240610_inline.jpg.large.jpg'
      ]
    };
  },
  computed: {
    localizedIntelTabs() {
      return [
        { label: this.t.intelligence.tabWriting },
        { label: this.t.intelligence.tabPlayground },
        { label: this.t.intelligence.tabGenmoji },
        { label: this.t.intelligence.tabSiri }
      ];
    },
    currentIntelImage() {
      return this.intelImages[this.activeTabIndex];
    }
  },
  methods: {
    toggleSiri() {
      this.isSiriActive = !this.isSiriActive;
    }
  }
}
</script>

<style scoped>
.intelligence-section {
  padding: 100px 24px;
  background: #000000;
  color: #f5f5f7;
  text-align: center;
  position: relative;
  overflow: hidden;
  margin-top: 5px;
}

.intel-container {
  max-width: 1120px;
  margin: 0 auto;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #f5f5f7;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 16px;
  border-radius: 980px;
  margin-bottom: 20px;
}

.ai-orb {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff3b30, #ff9500, #007aff, #af52de);
  box-shadow: 0 0 10px rgba(0, 122, 255, 0.8);
  animation: pulse 2s infinite ease-in-out;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.3);
    opacity: 1;
  }
}

.section-title {
  font-size: clamp(34px, 5.5vw, 56px);
  font-weight: 600;
  letter-spacing: -0.005em;
  line-height: 1.07143;
  color: #f5f5f7;
  margin-bottom: 4px;
}

.section-subtitle-gradient {
  font-size: clamp(30px, 5vw, 52px);
  line-height: 1.1;
  font-weight: 600;
  letter-spacing: 0.004em;
  background: linear-gradient(to right,
      #ff3b30,
      #ff9500,
      #ffcc00,
      #34c759,
      #007aff,
      #5856d6,
      #af52de);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  display: inline-block;
  margin-bottom: 20px;
}

.section-subtitle {
  max-width: 760px;
  margin: 0 auto 50px;
  font-size: 17px;
  line-height: 1.45;
  color: #86868b;
  font-weight: 400;
}

/* Siri Glow Simulator */
.siri-glow-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 70px;
}

.siri-phone-frame {
  position: relative;
  width: 320px;
  height: 600px;
  border-radius: 48px;
  background: #1c1c1e;
  padding: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8), 0 0 0 4px #2c2c2e;
  margin-bottom: 30px;
  transition: box-shadow 0.4s ease;
}

.siri-active .siri-phone-frame {
  box-shadow: 0 25px 80px rgba(0, 122, 255, 0.4),
              0 0 40px rgba(255, 59, 48, 0.3),
              0 0 0 4px #3a3a3c;
}

.phone-screen {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 38px;
  overflow: hidden;
  background: #000;
}

.screen-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.dynamic-island {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 90px;
  height: 24px;
  background: #000;
  border-radius: 20px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
}

.island-camera {
  width: 9px;
  height: 9px;
  background: #111;
  border-radius: 50%;
  box-shadow: inset 0 0 2px rgba(0, 122, 255, 0.5);
}

.siri-edge-glow {
  position: absolute;
  inset: 0;
  border-radius: 38px;
  border: 4px solid transparent;
  background: linear-gradient(135deg, #ff3b30, #ff9500, #34c759, #007aff, #af52de) border-box;
  -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
  mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: destination-out;
  mask-composite: exclude;
  pointer-events: none;
  animation: siriPulse 1.8s infinite alternate ease-in-out;
}

@keyframes siriPulse {
  0% {
    filter: drop-shadow(0 0 10px rgba(0, 122, 255, 0.8)) drop-shadow(0 0 20px rgba(255, 59, 48, 0.6));
    opacity: 0.7;
  }
  100% {
    filter: drop-shadow(0 0 25px rgba(52, 199, 89, 0.9)) drop-shadow(0 0 35px rgba(175, 82, 222, 0.8));
    opacity: 1;
  }
}

.siri-summon-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 980px;
  background: rgba(255, 255, 255, 0.12);
  color: #f5f5f7;
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 15px;
  cursor: pointer;
  margin-bottom: 24px;
  transition: all 0.25s ease;
}

.siri-summon-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.02);
}

.siri-summon-btn.active {
  background: linear-gradient(135deg, #007aff, #af52de);
  border-color: transparent;
  box-shadow: 0 0 20px rgba(0, 122, 255, 0.6);
}

.intel-tabs {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.intel-tab-btn {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #86868b;
  padding: 8px 18px;
  border-radius: 980px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.intel-tab-btn:hover {
  color: #f5f5f7;
  background: rgba(255, 255, 255, 0.1);
}

.intel-tab-btn.active {
  background: #ffffff;
  color: #000000;
  font-weight: 500;
  border-color: #ffffff;
}

/* Bento Grid */
.bento-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  text-align: left;
}

.bento-card {
  background: #161617;
  border-radius: 28px;
  padding: 36px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: transform 0.2s ease;
}

.bento-card:hover {
  transform: translateY(-4px);
}

.card-large {
  grid-column: span 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 30px;
}

.card-full {
  grid-column: span 2;
}

.card-eyebrow {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  color: #86868b;
  display: block;
  margin-bottom: 8px;
}

.card-title {
  font-size: 26px;
  font-weight: 600;
  color: #f5f5f7;
  line-height: 1.2;
  margin-bottom: 12px;
}

.card-desc {
  font-size: 15px;
  line-height: 1.45;
  color: #86868b;
  margin-bottom: 24px;
}

.bento-img {
  width: 100%;
  border-radius: 18px;
  display: block;
}

@media (max-width: 768px) {
  .bento-grid {
    grid-template-columns: 1fr;
  }

  .card-large {
    grid-column: span 1;
    grid-template-columns: 1fr;
  }

  .card-full {
    grid-column: span 1;
  }
}
</style>
